import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { defineMessages, type MessageDescriptor } from 'react-intl';

export const pricingPlansModalMessages = defineMessages({
  title: {
    defaultMessage: "There's a Heidi plan that's right for you",
    id: 'OHBiSA4BJa',
    description: 'Heading of the pricing plans modal',
  },
  teamUpgradeTitle: {
    defaultMessage: 'Upgrade your team',
    id: 'SQYxZSTg+w',
    description:
      'Heading of the pricing plans modal shown to a team on Practice Standard or Scribe Team',
  },
  salesTaxAndTermsNote: {
    defaultMessage: '*Prices exclude applicable sales tax. Terms and conditions apply.',
    id: 'QqFKcAXcr+',
    description:
      'Footnote of the pricing plans modal shown to a team on Practice Standard',
  },
  annually: {
    defaultMessage: 'Annually',
    id: '3b4jDfCp0w',
    description: 'Annual billing period tab on a pricing plan card',
  },
  monthly: {
    defaultMessage: 'Monthly',
    id: 'rfFsJWSNXb',
    description: 'Monthly billing period tab on a pricing plan card',
  },
  customPrice: {
    defaultMessage: 'Custom',
    id: 'hqq/gzuPci',
    description: 'Price shown on the Enterprise pricing plan card',
  },
  perUserMonth: {
    defaultMessage: 'user / month*',
    id: 'CEpPv3rRY1',
    description: 'Price caption under a paid plan amount',
  },
  current: {
    defaultMessage: 'Current',
    id: 'TlP5NRB5kx',
    description: 'Badge on the pricing plan card that matches the user’s plan',
  },
  // Used by the trial-end plan selection modal, which shares the base card.
  mostPopular: {
    defaultMessage: 'Most popular',
    id: 'e+e238OkVe',
    description: 'Badge on the recommended pricing plan card',
  },
  seeMore: {
    defaultMessage: 'See more',
    id: 'W+HA0q+OF/',
    description: 'Link to the public pricing page from a pricing plan card',
  },
  contactSalesToUpgrade: {
    defaultMessage: 'Contact Sales to upgrade',
    id: 'QZruQZV9JG',
    description: 'Tooltip when a non-Stripe-managed team cannot self-serve an upgrade',
  },
  contactTeamAdminToUpgrade: {
    defaultMessage: 'Contact your team admin to upgrade',
    id: 'RCf7LN3YOo',
    description: 'Tooltip when a non-admin team member cannot self-serve an upgrade',
  },
  keepClinician: {
    defaultMessage: 'Keep Clinician',
    id: '45O3HoSBpo',
    description: 'CTA on the Clinician card when it is the current plan',
  },
  getClinician: {
    defaultMessage: 'Get Clinician',
    id: 'iIj8y81RIi',
    description: 'CTA on the Clinician card to start checkout',
  },
  keepPractice: {
    defaultMessage: 'Keep Practice',
    id: '/LobRTC7cS',
    description: 'CTA on the Practice card when it is the current plan',
  },
  getPractice: {
    defaultMessage: 'Get Practice',
    id: 'gBCBdLTxFM',
    description: 'CTA on the Practice card to start checkout',
  },
  keepPracticeMax: {
    defaultMessage: 'Keep Practice Max',
    id: 'mXv8lk05ZY',
    description:
      'CTA on the Practice card, Max seat selected, when it is the current plan',
  },
  getPracticeMax: {
    defaultMessage: 'Get Practice Max',
    id: '0vA195eRpJ',
    description: 'CTA on the Practice card, Max seat selected, to start checkout',
  },
  seatStandard: {
    defaultMessage: 'Standard',
    id: 'Al9TfKxAMy',
    description: 'Standard seat tab on the Practice pricing plan card',
  },
  seatMax: {
    defaultMessage: 'Max',
    id: 'NxTZLhsXhG',
    description: 'Max seat tab on the Practice pricing plan card',
  },
  upgradeNow: {
    defaultMessage: 'Upgrade now',
    id: '3CK2vwSZNZ',
    description: 'CTA on a trial plan card to convert to a paid subscription',
  },
  featuresEverythingInFree: {
    defaultMessage: 'Everything in Free, plus:',
    id: 'onxWJQULih',
    description: 'Features list heading on the Clinician pricing plan card',
  },
  featuresEverythingInScribePlus: {
    defaultMessage: 'Everything in Scribe Plus, plus:',
    id: 'ieyAglaaZr',
    description: 'Features list heading on the Clinician card for Scribe Plus users',
  },
  featuresEverythingInClinician: {
    defaultMessage: 'Everything in Clinician, plus:',
    id: '+xNyUszyL3',
    description: 'Features list heading on Clinician Max and Practice cards',
  },
  featuresEverythingInPractice: {
    defaultMessage: 'Everything in Practice, plus:',
    id: 'obUGsJFcco',
    description:
      'Features list heading on the Practice card when the Max seat is selected',
  },
  featuresEverythingInScribeTeam: {
    defaultMessage: 'Everything in Scribe Team, plus:',
    id: 'mzmTr9o2sH',
    description: 'Features list heading on the Practice card for Scribe Team users',
  },
  availableRoles: {
    defaultMessage: 'Available roles',
    id: '8Qcn7SSZqy',
    description: 'Collapsible section heading on the Practice pricing plan card',
  },
  roleClinician: {
    defaultMessage: 'Clinician',
    id: 'qY04Uox5ls',
    description: 'Role listed in the Available roles section of the Practice card',
  },
  roleAdmin: {
    defaultMessage: 'Admin',
    id: 'usbihBiVTy',
    description: 'Role listed in the Available roles section of the Practice card',
  },
  roleAssistant: {
    defaultMessage: 'Assistant',
    id: 'tcTIk3Bdhm',
    description: 'Role listed in the Available roles section of the Practice card',
  },
  roleTemplateManager: {
    defaultMessage: 'Template manager',
    id: 'BHEnUUkMef',
    description: 'Role listed in the Available roles section of the Practice card',
  },
  salesTaxNote: {
    defaultMessage: '*Prices exclude applicable sales tax.',
    id: 'kV0P/0HZdH',
    description: 'Footnote under the pricing plan cards',
  },
  learnMore: {
    defaultMessage: 'Learn more',
    id: 'i9EVoUt0y+',
    description: 'Link to the public pricing page from the pricing plans modal footer',
  },
  trialDaysLeftBanner: {
    defaultMessage:
      'You have {count, plural, one {# day} other {# days}} left on your free trial of {plan}. <learnMore>Learn more.</learnMore>',
    id: 'YL01YhLaXN',
    description:
      'Pricing plans modal banner while the user is trialing, with at least one day left. {plan} is the translated name of the trial plan (e.g. Clinician, Practice). learnMore links to the usage-limits help article.',
  },
  trialHoursLeftBanner: {
    defaultMessage:
      'You have {count, plural, one {# hour} other {# hours}} left on your free trial of {plan}. <learnMore>Learn more.</learnMore>',
    id: '8B/H8Ly/QW',
    description:
      'Pricing plans modal banner while the user is trialing with under 24 hours left. {plan} is the translated name of the trial plan (e.g. Clinician, Practice). learnMore links to the usage-limits help article.',
  },
  complimentaryUsageBanner: {
    defaultMessage:
      'Enjoy $2,000 of usage to explore Heidi II until {date}. <learnMore>Learn more.</learnMore>',
    id: 'fR77eIyNc5',
    description:
      'Pricing plans modal banner during the Heidi II complimentary usage period. {date} is the billing cycle end. learnMore links to the usage-limits help article.',
  },
  extendTrial: {
    defaultMessage: 'Extend your free trial',
    id: 'rVobzX4KAf',
    description:
      'Button on the pricing plans modal trial banner that opens the trial-extension checklist',
  },
  talkToSales: {
    defaultMessage: 'Running a larger organization? {link}',
    id: 'LZOuF8lARf',
    description:
      'Enterprise sales prompt in the pricing plans modal footer. `{link}` is the Talk to sales CTA.',
  },
  talkToSalesCta: {
    defaultMessage: 'Talk to sales',
    id: 'qHfUJ5q+7H',
    description:
      'Linked CTA in the enterprise sales prompt of the pricing plans modal footer',
  },
  planNameFree: {
    defaultMessage: 'Free',
    id: 'GMw/xEjFdX',
    description: 'Name of the Free plan on a pricing plan card',
  },
  planNamePro: {
    defaultMessage: 'Scribe Plus',
    id: 'WQc0xvyvk4',
    description: 'Name of the Scribe Plus plan on a pricing plan card',
  },
  planNameProPlus: {
    defaultMessage: 'Scribe Team',
    id: 'nZaNRRXuS5',
    description: 'Name of the Scribe Team plan on a pricing plan card',
  },
  planNameEvidence: {
    defaultMessage: 'Evidence Plus',
    id: 'yVQglkazHL',
    description: 'Name of the Evidence Plus plan on a pricing plan card',
  },
  planNameClinicianPro: {
    defaultMessage: 'Clinician',
    id: '6gCWPdZHDT',
    description: 'Name of the Clinician plan on a pricing plan card',
  },
  planNameClinicianProPlus: {
    defaultMessage: 'Practice',
    id: 'Y8L0lOZutT',
    description: 'Name of the Practice plan on a pricing plan card',
  },
  planNameClinicianEnterprise: {
    defaultMessage: 'Enterprise',
    id: '8JcEtM/9yR',
    description: 'Name of the Clinician Enterprise plan on a pricing plan card',
  },
  planNameEnterprise: {
    defaultMessage: 'Enterprise',
    id: '+9+IN5MTTV',
    description: 'Name of the Enterprise plan on a pricing plan card',
  },
  planNameEvidenceTeam: {
    defaultMessage: 'Evidence Team',
    id: 'VaQu7IMnG2',
    description: 'Name of the Evidence Team plan on a pricing plan card',
  },
  planNameClinicianMax: {
    defaultMessage: 'Clinician Max',
    id: 'luhyWostzQ',
    description: 'Name of the Clinician Max plan on a pricing plan card',
  },
  planNamePracticeMax: {
    defaultMessage: 'Practice Max',
    id: 'uhDNYh4s5W',
    description: 'Name of the Practice Max plan on a pricing plan card',
  },
  planSubtitleClinician: {
    defaultMessage: 'Everything you need in the consult',
    id: 'TeHHpvziOD',
    description: 'Subtitle under the Clinician plan name on a pricing plan card',
  },
  planSubtitleClinicianMax: {
    defaultMessage: 'For clinicians using Agents daily',
    id: 'eHqReSKkf+',
    description: 'Subtitle under the Clinician Max plan name on a pricing plan card',
  },
  planSubtitlePractice: {
    defaultMessage: 'Run the whole practice on Heidi',
    id: 'mKW8w0esCV',
    description: 'Subtitle under the Practice plan name on a pricing plan card',
  },
  planSubtitlePracticeMax: {
    defaultMessage: 'For practices using Agents daily',
    id: 'QNTO7PsIh5',
    description: 'Subtitle under the Practice Max plan name on a pricing plan card',
  },
});

export const PRICING_PLAN_NAME_BY_TIER: Record<SubscriptionTier, MessageDescriptor> = {
  [SubscriptionTier.FREE]: pricingPlansModalMessages.planNameFree,
  [SubscriptionTier.PRO]: pricingPlansModalMessages.planNamePro,
  [SubscriptionTier.PRO_PLUS]: pricingPlansModalMessages.planNameProPlus,
  [SubscriptionTier.EVIDENCE]: pricingPlansModalMessages.planNameEvidence,
  [SubscriptionTier.CLINICIAN_PRO]: pricingPlansModalMessages.planNameClinicianPro,
  [SubscriptionTier.CLINICIAN_PRO_PLUS]:
    pricingPlansModalMessages.planNameClinicianProPlus,
  [SubscriptionTier.CLINICIAN_ENTERPRISE]:
    pricingPlansModalMessages.planNameClinicianEnterprise,
  [SubscriptionTier.ENTERPRISE]: pricingPlansModalMessages.planNameEnterprise,
  [SubscriptionTier.EVIDENCE_TEAM]: pricingPlansModalMessages.planNameEvidenceTeam,
};
